/**
 * Diretiva Vue para controlar visibilidade de elementos baseado em Admin Level
 * 
 * Esta diretiva permite controlar a exibição de elementos do DOM de forma declarativa
 * usando v-permission. O elemento será removido do DOM se o usuário não tiver
 * o nível de acesso necessário.
 * 
 * @example
 * <!-- Nível mínimo -->
 * <button v-permission:min="ADMIN_LEVELS.SUPER">Super Admin+</button>
 * 
 * @example
 * <!-- Nível exato -->
 * <button v-permission:exact="ADMIN_LEVELS.GOD">God Admin Only</button>
 * 
 * @example
 * <!-- Lista de níveis permitidos -->
 * <button v-permission:any="[ADMIN_LEVELS.MASTER, ADMIN_LEVELS.GOD]">Master ou God</button>
 * 
 * @example
 * <!-- Desabilitar ao invés de remover -->
 * <button v-permission:min.disable="ADMIN_LEVELS.SUPER">Desabilita se não for Super+</button>
 */

import { useAuthStore } from '@/stores/auth';
import { hasMinimumLevel } from '@/constants/adminLevels';

export const vPermission = {
  mounted(el, binding) {
    checkPermission(el, binding);
  },
  
  updated(el, binding) {
    checkPermission(el, binding);
  }
};

function checkPermission(el, binding) {
  const authStore = useAuthStore();
  const userLevel = authStore.adminLevelValue;
  const { arg, value, modifiers } = binding;
  
  let hasPermission = false;
  
  // Determina o tipo de verificação baseado no argumento
  switch (arg) {
    case 'min':
      // v-permission:min="level" - verifica nível mínimo
      hasPermission = hasMinimumLevel(userLevel, value);
      break;
      
    case 'exact':
      // v-permission:exact="level" - verifica nível exato
      hasPermission = userLevel === value;
      break;
      
    case 'any':
      // v-permission:any="[levels]" - verifica se está em algum dos níveis
      hasPermission = Array.isArray(value) && value.includes(userLevel);
      break;
      
    case 'range':
      // v-permission:range="[min, max]" - verifica se está dentro da faixa
      if (Array.isArray(value) && value.length === 2) {
        hasPermission = userLevel >= value[0] && userLevel <= value[1];
      }
      break;
      
    default:
      // Se não especificar argumento, assume nível mínimo
      hasPermission = hasMinimumLevel(userLevel, value);
  }
  
  // Aplica o resultado
  if (!hasPermission) {
    if (modifiers.disable) {
      // Modificador .disable - desabilita o elemento ao invés de remover
      el.disabled = true;
      el.style.opacity = '0.5';
      el.style.cursor = 'not-allowed';
      el.title = 'Você não tem permissão para esta ação';
    } else if (modifiers.hide) {
      // Modificador .hide - esconde mas mantém no DOM
      el.style.display = 'none';
    } else {
      // Comportamento padrão - remove do DOM
      el.style.display = 'none';
      // Alternativa mais agressiva: el.remove()
    }
  } else {
    // Restaura elemento se permissão foi concedida
    el.disabled = false;
    el.style.opacity = '';
    el.style.cursor = '';
    el.style.display = '';
    el.title = '';
  }
}

/**
 * Plugin para registrar a diretiva globalmente
 */
export default {
  install(app) {
    app.directive('permission', vPermission);
  }
};
